/**
 * buildNavTree — the workspace's navigable shape as PURE DATA.
 *
 * The viewer grew its navigation inside React (`WorkspaceNav` memos over store
 * state), which is fine for one surface and useless for every other: a native
 * IDE tree can't mount a React component to find out what a package contains.
 * So the shape lives here, over a `ResolvedModel`, with no DOM, no React, and
 * no store — the LSP answers a tree request with it, and any host renders it
 * with its own widgets.
 *
 * Four sections, because they are four different questions ("what is the
 * system made of" / "what can I look at" / "what happens" / "what is it made
 * of"), and a host lays them out as separate collapsible views.
 *
 * Ids are ADDRESSES: stable, human-meaningful, and exactly what a host puts in
 * a tab URI. They are the same spellings the rest of the toolchain accepts
 * (`spelledViewName`, dotted module paths), so `Show Dependents` on a tree row
 * and `--view-name` on the CLI name the same thing — which is why they come
 * from the engine's own identity helpers (`moduleDiffId`, `processIdentity`)
 * rather than being re-derived here. Those space-qualify (`Space::Name`), and
 * they have to: two spaces may each legally declare `Checkout`, and a bare
 * name would give both rows the same address, so a host would open one tab for
 * both and Copy Id would hand back something ambiguous.
 */
import {
  moduleDiffId,
  processIdentity,
  type ProcessDecl,
  type ResolvedModel,
  type ResolvedModule,
  type SourceSpan,
  type TypeDecl,
  type ViewDeclV2,
} from "@archlang/engine";
import { spelledViewName, viewRepresentation } from "./view-lookup.js";

export type NavNodeKind =
  | "space"
  | "module"
  | "interface"
  | "view"
  | "process"
  | "type";

export interface NavLocation {
  /** File URI/path the declaration was written in. */
  readonly file: string;
  /** 1-based, as engine spans already are — matching what an editor's "go to
   *  line" expects. A host targeting a 0-based API converts on its own side. */
  readonly line: number;
  readonly column: number;
}

export interface NavNode {
  readonly kind: NavNodeKind;
  /** Stable address — what a host encodes into a tab URI. */
  readonly id: string;
  readonly label: string;
  /** Dim trailing text: the module's type, a view's representation. */
  readonly detail?: string;
  readonly location?: NavLocation;
  /** Synthesized as a stub (§4.13) — a compiler TODO, not authored. Hosts
   *  render these differently; they are not navigable to real source. */
  readonly synthesized?: boolean;
  readonly children: readonly NavNode[];
}

export interface NavTree {
  /** Modules by space, each expanding to its interfaces and nested modules. */
  readonly architecture: readonly NavNode[];
  readonly views: readonly NavNode[];
  readonly processes: readonly NavNode[];
  readonly types: readonly NavNode[];
}

export const EMPTY_NAV_TREE: NavTree = {
  architecture: [],
  views: [],
  processes: [],
  types: [],
};

const NO_CHILDREN: readonly NavNode[] = [];

/** Shared with `entity-details`, so both answer a location the same way — a
 *  span without a file cannot be navigated to and must read as absent. */
export function locationOf(span: SourceSpan | undefined): NavLocation | undefined {
  if (!span?.file) return undefined;
  return { file: span.file, line: span.start.line, column: span.start.column };
}

function moduleNode(mod: ResolvedModule, parentPath: readonly string[]): NavNode {
  const path = [...parentPath, mod.name];
  const id = moduleDiffId(mod, parentPath);
  const interfaces = mod.interfaces.map(
    (iface): NavNode => ({
      kind: "interface",
      id: `${id}#${iface.name}`,
      label: iface.name,
      detail: iface.typeName,
      location: locationOf(iface.decl.source),
      synthesized: iface.synthesized,
      children: NO_CHILDREN,
    }),
  );
  const children = mod.modules.map((child) => moduleNode(child, path));
  return {
    kind: "module",
    id,
    label: mod.name,
    detail: mod.typeName,
    location: locationOf(mod.decl.source),
    synthesized: mod.synthesized,
    // Interfaces before nested modules: the module's own surface reads first,
    // then what it contains.
    children: [...interfaces, ...children],
  };
}

/**
 * Group the root modules by their landed space (v0.11 D1a). A workspace with
 * no spaces yields the modules directly — no pointless single "" group.
 */
function architectureOf(model: ResolvedModel): readonly NavNode[] {
  const roots = model.modules;
  const spaced = roots.some((m) => m.space && m.space.length > 0);
  if (!spaced) return roots.map((m) => moduleNode(m, []));

  const bySpace = new Map<string, ResolvedModule[]>();
  for (const mod of roots) {
    const key = (mod.space ?? []).join(".");
    const bucket = bySpace.get(key);
    if (bucket) bucket.push(mod);
    else bySpace.set(key, [mod]);
  }
  return [...bySpace.entries()]
    .sort(([a], [b]) => compare(a, b))
    .map(([space, mods]): NavNode => ({
      kind: "space",
      id: space,
      // A module that landed outside any space still needs a home.
      label: space === "" ? "(no space)" : space,
      children: mods.map((m) => moduleNode(m, [])),
      // The space itself is a name, not a declaration site — nothing to reveal.
    }));
}

function viewNode(
  view: ViewDeclV2,
  byName: ReadonlyMap<string, ViewDeclV2>,
): NavNode {
  return {
    kind: "view",
    id: spelledViewName(view),
    label: view.name,
    detail: viewRepresentation(view, byName),
    location: locationOf(view.source),
    children: NO_CHILDREN,
  };
}

function processNode(proc: ProcessDecl): NavNode {
  return {
    kind: "process",
    id: processIdentity(proc),
    label: proc.name,
    location: locationOf(proc.source),
    children: NO_CHILDREN,
  };
}

function typeNode(type: TypeDecl): NavNode {
  return {
    kind: "type",
    id: type.name,
    label: type.name,
    detail: type.parentType,
    location: locationOf(type.source),
    children: NO_CHILDREN,
  };
}

function compare(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function byLabel(a: NavNode, b: NavNode): number {
  return compare(a.label, b.label);
}

export function buildNavTree(model: ResolvedModel | null | undefined): NavTree {
  if (!model) return EMPTY_NAV_TREE;

  const views = model.views ?? [];
  // The same bare-name map the engine's instance merge consumes, so an
  // instance's representation resolves through its parent exactly as the board
  // renders it (see `viewRepresentation`).
  const byName = new Map<string, ViewDeclV2>();
  for (const v of views) if (!byName.has(v.name)) byName.set(v.name, v);

  return {
    // Architecture keeps DECLARATION order: nesting is the author's structure,
    // and re-sorting it would fight the mental model the source establishes.
    architecture: architectureOf(model),
    // Flat catalogues are lookups, so they sort.
    views: views
      .filter((v) => v.name)
      .map((v) => viewNode(v, byName))
      .sort(byLabel),
    // Anonymous processes carry an empty name and no id — they are a compiler
    // TODO, not something a reader can navigate to.
    processes: (model.processes ?? [])
      .filter((p) => !p.anonymous && p.name)
      .map(processNode)
      .sort(byLabel),
    types: (model.types ?? []).map(typeNode).sort(byLabel),
  };
}
